import { useState, useEffect, useRef } from 'react';

interface UseAnimatedCounterOptions {
  duration?: number;      // Animation duration in ms
  decimals?: number;      // Number of decimal places
  prefix?: string;        // Prefix like "$" or "€"
  suffix?: string;        // Suffix like "%" or " shares"
  easing?: 'linear' | 'easeOut' | 'easeInOut';
}

/**
 * Hook for animating number changes with smooth counting effect
 */
export function useAnimatedCounter(
  targetValue: number,
  options: UseAnimatedCounterOptions = {}
) {
  const {
    duration = 800,
    decimals = 0,
    prefix = '',
    suffix = '',
    easing = 'easeOut'
  } = options;

  const [displayValue, setDisplayValue] = useState(targetValue);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousValue = useRef(targetValue);
  const animationRef = useRef<number | null>(null);

  // Easing functions
  const easingFunctions = {
    linear: (t: number) => t,
    easeOut: (t: number) => 1 - Math.pow(1 - t, 3),
    easeInOut: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  };

  useEffect(() => {
    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startValue = previousValue.current;
    const endValue = targetValue;
    const startTime = performance.now();
    const easingFn = easingFunctions[easing];

    // Skip animation if values are the same
    if (startValue === endValue) {
      return;
    }

    setIsAnimating(true);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easingFn(progress);
      
      const currentValue = startValue + (endValue - startValue) * easedProgress;
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue);
        setIsAnimating(false);
        previousValue.current = endValue;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, easing]);

  // Format the display value
  const formattedValue = `${prefix}${displayValue.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`;

  return {
    value: displayValue,
    formattedValue,
    isAnimating
  };
}

/**
 * Simpler hook that just returns the formatted animated value
 */
export function useAnimatedValue(
  value: number,
  options: UseAnimatedCounterOptions = {}
) {
  const { formattedValue, isAnimating } = useAnimatedCounter(value, options);
  return { formattedValue, isAnimating };
}

export default useAnimatedCounter;

